/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/bolmateStatCard.scss';

import React, {useMemo, useState} from 'react';

import {Icon} from '@iconify/react';

import trendingUp from '@iconify/icons-feather/trending-up';

import trendingDown from '@iconify/icons-feather/trending-down';

import minus from '@iconify/icons-feather/minus';

import swapHorizontal from '@iconify/icons-mdi/swap-horizontal';

import percent from '@iconify/icons-feather/percent';

import classNames from 'classnames';

import {formatCurrency} from '../../../util/utils';

import {StatCardData} from '../../../util/interfaces';

import {StatTrend} from '../../../util/types';
import {BolmateTooltip} from '../index';

interface Props extends StatCardData {
    className?: string;
    tooltip?: string;
}

interface TrendStyle {
    icon: typeof trendingUp;
    arrow: string;
    className: string;
}

const trendConfig: Record<StatTrend, TrendStyle> = {
    up: {icon: trendingUp, arrow: '\u2191', className: 'positive'},
    down: {icon: trendingDown, arrow: '\u2193', className: 'negative'},
    neutral: {icon: minus, arrow: '\u2014', className: 'neutral'}
};

const invertedTrendConfig: Record<StatTrend, TrendStyle> = {
    up: {...trendConfig.up, className: 'negative'},
    down: {...trendConfig.down, className: 'positive'},
    neutral: {...trendConfig.neutral, className: 'neutral'}
};

export default function BolmateStatCard(props: Props) {
    const {alternate, truncateThreshold, className, tooltip} = props;
    const [isAlternate, setIsAlternate] = useState(false);
    const [showPercentMode, setShowPercentMode] = useState(false);

    const data = isAlternate && alternate ? alternate : props;
    const {label, value, prevValue, valueType = 'generic', invertTrend} = data;
    const [diff, percentage] = useMemo(() => {
        let percentage: number;
        if (value === prevValue) percentage = 0;
        else if (value === 0) percentage = -100;
        else if (prevValue === 0) percentage = 100;
        else percentage = ((value - prevValue) / prevValue) * 100;
        return [value - prevValue, Math.abs(percentage).toFixed(1)];
    }, [value, prevValue]);
    const effectiveTrend = diff === 0 ? 'neutral' : diff < 0 ? 'down' : 'up';
    const config = invertTrend ? invertedTrendConfig[effectiveTrend] : trendConfig[effectiveTrend];

    function renderValue(value: number) {
        let truncated = false;
        if (value === 0) return '\u2014';
        if (truncateThreshold && truncateThreshold >= 1000 && value > truncateThreshold) {
            value = Number((value / 1000).toFixed(1));
            truncated = true;
        }
        if (valueType === 'currency') {
            if (truncated) return formatCurrency(value) + 'K';
            return formatCurrency(value);
        } else if (valueType === 'percentage') {
            return `${value.toFixed(1).replace('.', ',')}%`;
        }
        if (truncated) return `${value}K`;
        return value;
    }

    function renderLabel() {
        if (tooltip)
            return (
                <BolmateTooltip text={tooltip}>
                    <span className='stat-card-label dashed' title={label}>
                        {label}
                    </span>
                </BolmateTooltip>
            );
        return (
            <span className='stat-card-label' title={label}>
                {label}
            </span>
        );
    }

    return (
        <div className={classNames('bolmate-stat-card', className)}>
            <div className='stat-card-header'>
                {renderLabel()}
                <div className='stat-card-actions'>
                    <div className={classNames('trend-icon-box', config.className)}>
                        <Icon icon={config.icon} width={16} height={16} />
                    </div>
                    <div className='toggle-actions'>
                        {!!alternate && (
                            <button
                                className={classNames('toggle-btn', isAlternate && 'active')}
                                onClick={() => setIsAlternate(!isAlternate)}
                                title='Weergave wisselen'
                            >
                                <Icon icon={swapHorizontal} width={12} height={12} />
                            </button>
                        )}
                        <button
                            className={classNames('toggle-btn', showPercentMode && 'active')}
                            onClick={() => setShowPercentMode(!showPercentMode)}
                            title='Percentage weergave'
                        >
                            <Icon icon={percent} width={10} height={10} />
                        </button>
                    </div>
                </div>
            </div>

            <div className='stat-card-value-row'>
                <span className='stat-card-main-value'>
                    {showPercentMode
                        ? Number(percentage) === 0
                            ? `${percentage}%`
                            : `${config.arrow} ${percentage}%`
                        : renderValue(value)}
                </span>
                <span className={classNames('stat-card-badge', showPercentMode ? 'muted' : config.className)}>
                    {showPercentMode
                        ? renderValue(value)
                        : Number(percentage) === 0
                          ? `${percentage}%`
                          : `${config.arrow} ${percentage}%`}
                </span>
            </div>

            <div className='stat-card-footer'>
                <span className='stat-card-prev'>Vorige: {renderValue(prevValue)}</span>
                <span className={classNames('stat-card-comparison', config.className)}>
                    {diff === 0 ? '\u2014' : `(${diff < 0 ? '-' : '+'} ${renderValue(Math.abs(diff))})`}
                </span>
            </div>
        </div>
    );
}
